
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DeviceStatusFilter } from "@/components/dashboard/DeviceStatusFilter";
import DeviceList from "@/components/DeviceList";
import { useDevices, DEVICES_QUERY_KEY } from "@/hooks/useDevices";
import { getDeviceStatus } from "@/utils/deviceStatus";
import { broadcastDeviceUpdate } from "@/services/deviceService";
import { seedMockDevices } from "@/services/deviceService";

const Dashboard = () => {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { data: devices = [], isLoading } = useDevices();
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Perform one-time database initialization on app startup
  useEffect(() => {
    const initializeDatabase = async () => {
      if (!isInitialized) {
        const { data, error } = await supabase
          .from('devices')
          .select('id', { count: 'exact' });
        
        const deviceCount = data?.length || 0;
        console.log(`Database check - device count: ${deviceCount}`);
        
        if (deviceCount < 10) {
          console.log('Initializing database with mock devices...');
          await seedMockDevices();
          // Force an immediate refresh
          queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
          queryClient.refetchQueries({ queryKey: DEVICES_QUERY_KEY });
        }
        
        setIsInitialized(true);
      }
    };
    
    initializeDatabase();
  }, [queryClient, isInitialized]);
  
  // Phase 3: Optimize real-time subscriptions with debouncing
  useEffect(() => {
    console.log('Setting up real-time subscription for devices...');
    let debounceTimer: ReturnType<typeof setTimeout>;
    
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'devices'
        },
        (payload) => {
          console.log('Received real-time update:', payload);
          
          // Phase 3: Debounce rapid updates
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            // Only invalidate queries, let React Query handle refetching
            queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
            
            // Broadcast to other tabs
            broadcastDeviceUpdate();
          }, 300); // 300ms debounce
        }
      )
      .subscribe();

    // Phase 3: Simplified event handlers
    const handleCacheUpdated = () => {
      console.log('Cache update event detected');
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
      }, 300);
    };
    
    window.addEventListener('device-cache-updated', handleCacheUpdated);
    
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'device-cache-updated') {
        console.log('Storage change detected from another tab');
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
        }, 300);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      console.log('Cleaning up real-time subscription...');
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
      window.removeEventListener('device-cache-updated', handleCacheUpdated);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [queryClient]);

  // Calculate device counts
  const counts = devices.reduce((acc, device) => {
    const status = getDeviceStatus(device.last_seen || device.created_at, device.status);
    acc[status]++;
    acc.total++;
    return acc;
  }, { active: 0, inactive: 0, pending: 0, total: 0 });

  const handleStatusClick = (status: string | null) => {
    setStatusFilter(status === statusFilter ? null : status);
  };

  return (
    <div className="min-h-screen bg-background w-full">
      <div className="w-full px-6 py-6">
        <DashboardHeader />
        <DeviceStatusFilter
          statusFilter={statusFilter}
          onStatusClick={handleStatusClick}
          counts={counts}
          isLoading={isLoading}
        />
        <DeviceList statusFilter={statusFilter} onStatusClick={handleStatusClick} />
      </div>
    </div>
  );
};

export default Dashboard;
